import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '../../services/api';

/**
 * Quién del equipo ve y gestiona los planes de pago de sus expedientes AR
 * (docs/planes-de-pago-fase-1-plan.md, §5). Ver y gestionar van juntos.
 *
 * Al revés que la Facturación, el gestor lo tiene encendido por defecto: es
 * quien acuerda el plan con el deudor. El operator, apagado. El servidor decide
 * (`canManagePaymentPlan`); esto solo lo configura.
 */
const ROLES = [
  { role: 'gestor', label: 'Los gestores ven y gestionan los planes de pago de sus expedientes' },
  { role: 'operator', label: 'Los operadores ven y gestionan los planes de pago de sus expedientes' },
];

const TeamPaymentPlansAccess = ({ companyId, isAdmin = false }) => {
  const [datos, setDatos] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get('/team-permissions', { params: isAdmin ? { company_id: companyId } : {} });
      setDatos(data.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }, [companyId, isAdmin]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const alternar = async role => {
    const nuevo = !datos.payment_plans?.[role];
    setGuardando(true);
    try {
      const { data } = await api.put('/team-permissions', {
        ...(isAdmin ? { company_id: companyId } : {}),
        payment_plans: { [role]: nuevo },
      });
      setDatos(data.data);
      toast.success(nuevo ? 'Ya pueden gestionar los planes de pago' : 'Ya no ven los planes de pago');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setGuardando(false);
    }
  };

  if (!datos?.payment_plans) return null;

  return (
    <div className='bg-white rounded-lg border border-slate-200 p-6'>
      <div className='flex items-center mb-1'>
        <CalendarDaysIcon className='h-5 w-5 text-blue-600 mr-2' />
        <h3 className='text-lg font-medium text-slate-900'>Planes de pago del equipo</h3>
      </div>
      <p className='text-sm text-slate-500 mb-4'>
        Quien lo tenga encendido ve los planes de pago de los expedientes que tiene asignados, y puede darlos de alta y
        cancelarlos. Por defecto, los gestores sí y los operadores no.
      </p>
      <div className='divide-y divide-slate-100'>
        {ROLES.map(r => (
          <label key={r.role} className='flex items-center justify-between gap-3 py-2 text-sm text-slate-700'>
            {r.label}
            <input
              type='checkbox'
              checked={Boolean(datos.payment_plans[r.role])}
              disabled={guardando || !datos.can_edit}
              onChange={() => alternar(r.role)}
              aria-label={r.label}
            />
          </label>
        ))}
      </div>
    </div>
  );
};

export default TeamPaymentPlansAccess;
